import { useEffect, useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { useRadioGroupOptions } from '@alltrails/denali/components/RadioGroup';
import { RadioFilterConfig } from '@alltrails/denali/src/components/FilterBar';
import AdminExploreFilters from '../../types/AdminExploreFilters';

const all = 'all' as const;
type ClosedStatus = 'open' | 'closed';
type OptionValue = typeof all | ClosedStatus;

const values: OptionValue[] = [all, 'open', 'closed'];

const getLabel = (intl: IntlShape, value: OptionValue) => {
  if (value === all) {
    return intl.formatMessage({ defaultMessage: 'All' });
  } else if (value === 'closed') {
    return intl.formatMessage({ defaultMessage: 'Closed' });
  } else {
    return intl.formatMessage({ defaultMessage: 'Open' });
  }
};

const useAdminClosedFilterConfig = (
  adminExploreFilters: AdminExploreFilters,
  onFiltersChange: (updatedFilters: AdminExploreFilters) => void
): RadioFilterConfig => {
  const intl = useIntl();

  const selected = useMemo<OptionValue>(() => {
    if (adminExploreFilters.isClosed === undefined) {
      return all;
    } else if (adminExploreFilters.isClosed) {
      return 'closed';
    }
    return 'open';
  }, [adminExploreFilters.isClosed]);
  const { onChange, options, clearOptions, selectedOption } = useRadioGroupOptions(
    values.map(value => ({
      selected: value === selected,
      testId: value,
      text: getLabel(intl, value),
      value
    })),
    all
  );

  useEffect(() => {
    if (selected !== selectedOption?.value) {
      onChange(selected);
    }
    // Only want to update when we get a new value
  }, [selected]);

  useEffect(() => {
    const typedValue = (selectedOption?.value ?? all) as OptionValue;
    const newFilters = { ...adminExploreFilters };
    delete newFilters.isClosed;

    // Do nothing is all is selected
    if (typedValue === 'open') {
      newFilters.isClosed = false;
    } else if (typedValue === 'closed') {
      newFilters.isClosed = true;
    }

    onFiltersChange(newFilters);

    // Only want to trigger this the selected value changes
  }, [selectedOption?.value]);

  return {
    type: 'radio',
    id: 'closed',
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOption && selectedOption.value !== all ? [selectedOption.text] : undefined,
      testId: 'closed-filter-chip',
      unselectedText: intl.formatMessage({ defaultMessage: 'Closed' })
    },
    radioGroup: {
      labelText: intl.formatMessage({ defaultMessage: 'Closed' }),
      name: 'closed',
      onChange,
      options,
      testId: 'closed-filter'
    }
  };
};

export default useAdminClosedFilterConfig;
